import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Save, Loader2, Plus, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";
import TagPicker from "@/components/studio/TagPicker";

const RETENTIONS = [
  { id: "temporary", label: "Temporary (one-time)", hint: "For one-off jobs. Kept until you delete it." },
  { id: "30day", label: "30 days", hint: "Flagged for expiry 30 days from now — remind the client to renew." },
  { id: "permanent", label: "Permanent", hint: "Kept indefinitely (reusable / brand artwork)." },
];

// Save a generated production file into the client File Library (Vault).
export const SaveToLibraryButton = ({ fileId, defaultLabel = "", matchClientName = "", size = "sm", variant = "outline", iconOnly = false }) => {
  const [open, setOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [orders, setOrders] = useState([]);
  const [clientId, setClientId] = useState("");
  const [orderId, setOrderId] = useState("");
  const [label, setLabel] = useState(defaultLabel);
  const [retention, setRetention] = useState("30day");
  const [tags, setTags] = useState([]);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [adding, setAdding] = useState(false);
  const [nc, setNc] = useState({ name: "", email: "", phone: "" });

  useEffect(() => { setLabel(defaultLabel); }, [defaultLabel]);
  useEffect(() => { setSaved(false); }, [fileId]);

  useEffect(() => {
    if (!open) return;
    api.get("/clients").then((r) => {
      const cs = r.data || [];
      setClients(cs);
      if (matchClientName && !clientId) {
        const m = cs.find((c) => (c.name || "").trim().toLowerCase() === matchClientName.trim().toLowerCase());
        if (m) setClientId(m.id);
      }
    }).catch(() => {});
    api.get("/orders").then((r) => setOrders(r.data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const quickAdd = async () => {
    if (!nc.name.trim()) { toast.error("Client name is required"); return; }
    try {
      const { data } = await api.post("/clients", nc);
      setClients((c) => [...c, data].sort((a, b) => a.name.localeCompare(b.name)));
      setClientId(data.id);
      setAdding(false);
      setNc({ name: "", email: "", phone: "" });
      toast.success("Client added");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/files/${fileId}/library`, {
        client_id: clientId || null, order_id: orderId || null, label, retention, tags,
      });
      setSaved(true);
      setOpen(false);
      toast.success("Saved to file library");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <>
      {iconOnly ? (
        <button onClick={() => setOpen(true)} disabled={!fileId} title="Save to library"
          className="text-slate-400 hover:text-[#2495D3] p-1 disabled:opacity-40" data-testid="save-to-library-btn">
          {saved ? <CheckCircle2 size={14} className="text-emerald-500" /> : <Save size={14} />}
        </button>
      ) : (
        <Button size={size} variant={variant} onClick={() => setOpen(true)} disabled={!fileId} data-testid="save-to-library-btn">
          {saved ? <CheckCircle2 size={15} className="mr-1.5 text-emerald-500" /> : <Save size={15} className="mr-1.5" />}
          {saved ? "Saved to library" : "Save to library"}
        </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92vh] overflow-y-auto" data-testid="save-library-dialog">
          <DialogHeader>
            <DialogTitle>Save file to library</DialogTitle>
            <DialogDescription>Attach this production file to a client (and optionally an order), tag it, and choose how long to keep it.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Client */}
            <div>
              <label className="text-[11px] font-mono uppercase text-slate-500 mb-1 block">Client</label>
              {!adding ? (
                <div className="flex items-center gap-2">
                  <select className="h-10 rounded-lg border px-2 text-sm flex-1 bg-white" value={clientId}
                    onChange={(e) => setClientId(e.target.value)} data-testid="library-client-select">
                    <option value="">— No client —</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  <Button type="button" size="sm" variant="outline" onClick={() => setAdding(true)} data-testid="library-add-client-toggle"><Plus size={14} className="mr-1" /> New</Button>
                </div>
              ) : (
                <div className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid="library-new-client">
                  <div className="flex items-center justify-between"><span className="text-xs font-semibold text-slate-600">New client</span>
                    <button type="button" onClick={() => setAdding(false)} className="text-slate-400 hover:text-slate-600"><X size={15} /></button></div>
                  <Input placeholder="Name *" value={nc.name} onChange={(e) => setNc({ ...nc, name: e.target.value })} data-testid="library-new-client-name" />
                  <div className="flex gap-2">
                    <Input placeholder="Email" value={nc.email} onChange={(e) => setNc({ ...nc, email: e.target.value })} data-testid="library-new-client-email" />
                    <Input placeholder="Phone" value={nc.phone} onChange={(e) => setNc({ ...nc, phone: e.target.value })} data-testid="library-new-client-phone" />
                  </div>
                  <Button type="button" size="sm" onClick={quickAdd} data-testid="library-new-client-save">Add client</Button>
                </div>
              )}
            </div>

            {/* Order (optional) */}
            <div>
              <label className="text-[11px] font-mono uppercase text-slate-500 mb-1 block">Order (optional)</label>
              <select className="h-10 rounded-lg border px-2 text-sm w-full bg-white" value={orderId}
                onChange={(e) => setOrderId(e.target.value)} data-testid="library-order-select">
                <option value="">— No order —</option>
                {orders.map((o) => <option key={o.id} value={o.id}>{(o.customer_name || o.user_email || "Order")} · #{o.id.slice(-6)}</option>)}
              </select>
            </div>

            {/* Label */}
            <div>
              <label className="text-[11px] font-mono uppercase text-slate-500 mb-1 block">Label / note</label>
              <Input placeholder="e.g. 24x36 banner — front" value={label} onChange={(e) => setLabel(e.target.value)} data-testid="library-label" />
            </div>

            {/* Tags */}
            <div>
              <label className="text-[11px] font-mono uppercase text-slate-500 mb-1 block">Tags</label>
              <TagPicker value={tags} onChange={setTags} testid="library-tags" />
            </div>

            {/* Retention */}
            <div>
              <label className="text-[11px] font-mono uppercase text-slate-500 mb-1 block">Retention</label>
              <div className="grid grid-cols-3 gap-2" data-testid="library-retention">
                {RETENTIONS.map((r) => (
                  <button key={r.id} type="button" onClick={() => setRetention(r.id)} title={r.hint}
                    className={`px-2 py-2 rounded-lg text-xs border text-center transition-colors ${retention === r.id ? "border-[#2495D3] bg-[#2495D3]/10 text-[#2495D3] font-semibold" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}
                    data-testid={`library-retention-${r.id}`}>{r.label}</button>
                ))}
              </div>
              <p className="text-[11px] text-slate-400 mt-1.5">{RETENTIONS.find((r) => r.id === retention)?.hint}</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="library-cancel">Cancel</Button>
            <Button onClick={save} disabled={busy} data-testid="library-save">
              {busy ? <Loader2 size={15} className="animate-spin mr-1.5" /> : <Save size={15} className="mr-1.5" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SaveToLibraryButton;
